import { ApiHttpError, adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  FormDrawer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
  useMediaQuery,
} from '@avila-roffe/components-web';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BadgeCheck,
  BarChart2,
  Bell,
  Binary,
  BookOpen,
  Bot,
  Box,
  Braces,
  Brain,
  Briefcase,
  Building2,
  Cloud,
  Code2,
  Cpu,
  CreditCard,
  Database,
  DollarSign,
  Expand,
  FileCode,
  FileText,
  Fingerprint,
  Gauge,
  GitBranch,
  Globe,
  HardDrive,
  Home,
  ImagePlus,
  Key,
  Layers,
  LineChart,
  Lock,
  Mail,
  MessageSquare,
  MonitorDot,
  Network,
  Package,
  Pencil,
  PieChart,
  PlugZap,
  Receipt,
  RefreshCw,
  Rocket,
  ScrollText,
  Search,
  Server,
  Settings,
  Shield,
  ShoppingCart,
  Shuffle,
  Sparkles,
  Star,
  Terminal,
  TrendingUp,
  UserCog,
  Users,
  Wand2,
  Webhook,
  Workflow,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useModelRegistry } from '../features/agents/hooks/use-agents';

// ─── Icon catalogue ───────────────────────────────────────────────────────────

export const APP_ICON_CATEGORIES: { label: string; icons: { name: string; Icon: LucideIcon }[] }[] =
  [
    {
      label: 'Infrastructure',
      icons: [
        { name: 'Server', Icon: Server },
        { name: 'Database', Icon: Database },
        { name: 'HardDrive', Icon: HardDrive },
        { name: 'Cloud', Icon: Cloud },
        { name: 'Cpu', Icon: Cpu },
        { name: 'Network', Icon: Network },
        { name: 'MonitorDot', Icon: MonitorDot },
      ],
    },
    {
      label: 'Development',
      icons: [
        { name: 'Code2', Icon: Code2 },
        { name: 'Terminal', Icon: Terminal },
        { name: 'GitBranch', Icon: GitBranch },
        { name: 'Package', Icon: Package },
        { name: 'FileCode', Icon: FileCode },
        { name: 'Braces', Icon: Braces },
        { name: 'Binary', Icon: Binary },
        { name: 'Wrench', Icon: Wrench },
      ],
    },
    {
      label: 'Security',
      icons: [
        { name: 'Shield', Icon: Shield },
        { name: 'Lock', Icon: Lock },
        { name: 'Key', Icon: Key },
        { name: 'Fingerprint', Icon: Fingerprint },
        { name: 'BadgeCheck', Icon: BadgeCheck },
      ],
    },
    {
      label: 'AI & Automation',
      icons: [
        { name: 'Bot', Icon: Bot },
        { name: 'Brain', Icon: Brain },
        { name: 'Sparkles', Icon: Sparkles },
        { name: 'Wand2', Icon: Wand2 },
        { name: 'Workflow', Icon: Workflow },
        { name: 'Zap', Icon: Zap },
      ],
    },
    {
      label: 'Analytics',
      icons: [
        { name: 'BarChart2', Icon: BarChart2 },
        { name: 'PieChart', Icon: PieChart },
        { name: 'TrendingUp', Icon: TrendingUp },
        { name: 'Activity', Icon: Activity },
        { name: 'Gauge', Icon: Gauge },
        { name: 'LineChart', Icon: LineChart },
      ],
    },
    {
      label: 'Communication',
      icons: [
        { name: 'MessageSquare', Icon: MessageSquare },
        { name: 'Mail', Icon: Mail },
        { name: 'Bell', Icon: Bell },
        { name: 'Webhook', Icon: Webhook },
        { name: 'PlugZap', Icon: PlugZap },
      ],
    },
    {
      label: 'Commerce',
      icons: [
        { name: 'DollarSign', Icon: DollarSign },
        { name: 'ShoppingCart', Icon: ShoppingCart },
        { name: 'CreditCard', Icon: CreditCard },
        { name: 'Receipt', Icon: Receipt },
      ],
    },
    {
      label: 'People & Org',
      icons: [
        { name: 'Users', Icon: Users },
        { name: 'Building2', Icon: Building2 },
        { name: 'Briefcase', Icon: Briefcase },
        { name: 'UserCog', Icon: UserCog },
      ],
    },
    {
      label: 'Content',
      icons: [
        { name: 'FileText', Icon: FileText },
        { name: 'BookOpen', Icon: BookOpen },
        { name: 'ScrollText', Icon: ScrollText },
        { name: 'Search', Icon: Search },
      ],
    },
    {
      label: 'General',
      icons: [
        { name: 'Globe', Icon: Globe },
        { name: 'Home', Icon: Home },
        { name: 'Star', Icon: Star },
        { name: 'Rocket', Icon: Rocket },
        { name: 'Box', Icon: Box },
        { name: 'Layers', Icon: Layers },
        { name: 'Settings', Icon: Settings },
        { name: 'Shuffle', Icon: Shuffle },
        { name: 'RefreshCw', Icon: RefreshCw },
      ],
    },
  ];

export const APP_ICON_OPTIONS = APP_ICON_CATEGORIES.flatMap((c) => c.icons);

export const APP_ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  APP_ICON_OPTIONS.map(({ name, Icon }) => [name, Icon]),
);

// ─── Colour swatches ─────────────────────────────────────────────────────────

const BG_SWATCHES = [
  { label: 'None', value: '' },
  { label: 'Ink', value: '#1F2937' },
  { label: 'Navy', value: '#1e3a8a' },
  { label: 'Blue', value: '#1d4ed8' },
  { label: 'Violet', value: '#7c3aed' },
  { label: 'Teal', value: '#0f766e' },
  { label: 'Green', value: '#15803d' },
  { label: 'Amber', value: '#b45309' },
  { label: 'Orange', value: '#c2410c' },
  { label: 'Rose', value: '#be123c' },
  { label: 'Pink', value: '#be185d' },
  { label: 'Slate', value: '#475569' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toRec(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};
}

const defaultAiModel = 'gpt-image-2';

// ─── Props ────────────────────────────────────────────────────────────────────

interface AppAvatarEditorProps {
  logoUrl?: string | null;
  pendingFile?: File | null;
  onFileSelect: (file: File) => void;
  onClearImage?: () => void;
  avatarIcon?: string | null;
  onIconChange: (icon: string | null) => void;
  avatarBgColor?: string | null;
  onBgColorChange: (color: string) => void;
  fallback?: string;
  /** Drawer title. Defaults to "Application Avatar". */
  title?: string;
  /** When false, hides the icon picker tab and background colour controls. */
  enableIcon?: boolean;
  /** When provided, shows the AI generation checkbox and panel. */
  aiGenerate?: {
    /** Agent identifiers — used to look up the style guideline server-side. */
    agentAppSlug?: string;
    agentName?: string;
    /**
     * Pre-fills the description textarea (what the entity IS/DOES).
     * The BFF wraps this in the full style-guideline template.
     * For non-agent contexts, pass a plain description (e.g. dish name + ingredients).
     */
    agentDescription?: string;
  };
}

// ─── Avatar preview ───────────────────────────────────────────────────────────

function AvatarPreview({
  displayUrl,
  avatarIcon,
  avatarBgColor,
  fallback,
  size = 'md',
}: {
  displayUrl?: string | null;
  avatarIcon?: string | null;
  avatarBgColor?: string | null;
  fallback?: string;
  size?: 'sm' | 'md';
}) {
  const IconComponent = avatarIcon ? APP_ICON_MAP[avatarIcon] : null;
  const bgStyle = avatarBgColor ? { backgroundColor: avatarBgColor } : {};
  const sizeClass = size === 'sm' ? 'size-12' : 'size-16';
  const iconClass = size === 'sm' ? 'size-6' : 'size-8';

  return (
    <div
      className={`${sizeClass} overflow-hidden rounded-[var(--radius)] border flex items-center justify-center shrink-0`}
      style={bgStyle}
    >
      {IconComponent ? (
        <IconComponent
          className={iconClass}
          style={{ color: avatarBgColor ? '#ffffff' : 'currentColor' }}
        />
      ) : displayUrl ? (
        <img src={displayUrl} alt="Avatar" className="size-full object-cover" />
      ) : (
        <span className="text-base font-semibold text-muted-foreground select-none">
          {fallback}
        </span>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AppAvatarEditor({
  logoUrl,
  pendingFile,
  onFileSelect,
  onClearImage,
  avatarIcon,
  onIconChange,
  avatarBgColor,
  onBgColorChange,
  fallback = '?',
  title = 'Application Avatar',
  enableIcon = true,
  aiGenerate,
}: AppAvatarEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const colorRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);

  // Trigger preview URL (from pending file)
  const [triggerPreviewUrl, setTriggerPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingFile) {
      setTriggerPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setTriggerPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingFile]);

  // Draft state — committed on Apply
  const [draftMode, setDraftMode] = useState<'image' | 'icon'>('image');
  const [draftIcon, setDraftIcon] = useState<string | null>(null);
  const [draftBgColor, setDraftBgColor] = useState('');
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // AI generation
  const [useAiGenerate, setUseAiGenerate] = useState(false);
  const [aiDescription, setAiDescription] = useState(''); // user-editable description only
  const [aiModel, setAiModel] = useState(defaultAiModel);
  const [aiCount, setAiCount] = useState(1);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<{
    message: string;
    traceId?: string;
    code?: string;
  } | null>(null);
  const [aiImages, setAiImages] = useState<{ url: string; file: File }[]>([]);
  const [selectedAiIdx, setSelectedAiIdx] = useState<number | null>(null);

  const [promptExpanded, setPromptExpanded] = useState(false);
  const isDesktop = useMediaQuery('(hover: hover) and (pointer: fine)');

  const { data: registry = [] } = useModelRegistry();
  const imageModels = registry.filter((m) => m.supportsImageGeneration);

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync only when dialog opens
  useEffect(() => {
    if (open) {
      setDraftMode(enableIcon && avatarIcon ? 'icon' : 'image');
      setDraftIcon(avatarIcon ?? null);
      setDraftBgColor(avatarBgColor ?? '');
      setDraftFile(pendingFile ?? null);
      setDraftPreviewUrl(null);
      setFileError(null);
      setAiGenerating(false);
      setAiError(null as null);
      setAiImages([]);
      setSelectedAiIdx(null);
      setAiModel(defaultAiModel);
      setAiCount(1);
      setUseAiGenerate(false);
      // Pre-fill description textarea with the agent's description (user edits just this part)
      setAiDescription(aiGenerate?.agentDescription ?? '');
    }
  }, [open]);

  useEffect(() => {
    if (!draftFile) {
      setDraftPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(draftFile);
    setDraftPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draftFile]);

  function dataUrlToFile(imageUrl: string): File {
    const commaIdx = imageUrl.indexOf(',');
    const mime = imageUrl.slice(5, imageUrl.indexOf(';'));
    const b64 = imageUrl.slice(commaIdx + 1);
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: mime || 'image/png' });
    return new File([blob], `ai-avatar-${Date.now()}.png`, { type: blob.type });
  }

  async function handleAiGenerate() {
    if (!aiGenerate) return;
    setAiGenerating(true);
    setAiError(null);
    try {
      // Step 1: enqueue — returns {jobId} immediately (202)
      const startResp = await adminBffFetch<unknown>('/api/v1/avatars/generate', {
        method: 'POST',
        body: JSON.stringify({
          agentAppSlug: aiGenerate.agentAppSlug ?? '',
          agentName: aiGenerate.agentName ?? '',
          // Send description only — BFF wraps it in the full style-guideline template
          context: { name: aiGenerate.agentName ?? '', description: aiDescription.trim() },
          model: aiModel,
          n: aiCount,
        }),
      });
      const jobId =
        typeof toRec(toRec(startResp).data).jobId === 'string'
          ? (toRec(toRec(startResp).data).jobId as string)
          : '';
      if (!jobId) throw new Error('No job ID returned');

      // Step 2: poll every 2s until done or failed
      const imageUrls = await new Promise<string[]>((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const pollResp = await adminBffFetch<unknown>(`/api/v1/avatars/jobs/${jobId}`);
            const job = toRec(toRec(pollResp).data);
            const status = typeof job.status === 'string' ? job.status : '';
            if (status === 'done') {
              clearInterval(interval);
              const urls = Array.isArray(job.imageUrls) ? (job.imageUrls as string[]) : [];
              if (urls.length === 0) {
                reject(new Error('No images in completed job'));
                return;
              }
              resolve(urls);
            } else if (status === 'failed') {
              clearInterval(interval);
              reject(new Error(typeof job.error === 'string' ? job.error : 'Generation failed'));
            }
            // status === 'pending' → keep polling
          } catch (pollErr) {
            clearInterval(interval);
            reject(pollErr);
          }
        }, 2000);
      });

      // Step 3: decode each base64 data URL → File, add all to gallery
      const newEntries = imageUrls.map((url) => {
        const file = dataUrlToFile(url);
        return { url: URL.createObjectURL(file), file };
      });

      setAiImages((prev) => {
        const next = [...prev, ...newEntries];
        const lastIdx = next.length - 1;
        setSelectedAiIdx(lastIdx);
        setDraftFile(next[lastIdx].file);
        return next;
      });
    } catch (err) {
      const traceId = err instanceof ApiHttpError ? err.traceId : undefined;
      const code = err instanceof ApiHttpError ? err.code : undefined;
      setAiError({
        message: err instanceof Error ? err.message : 'Generation failed',
        traceId,
        code,
      });
    } finally {
      setAiGenerating(false);
    }
  }

  function selectAiImage(idx: number) {
    setSelectedAiIdx(idx);
    setDraftFile(aiImages[idx].file);
  }

  function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setFileError('Only image files are allowed.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setFileError('Image must be smaller than 2 MB.');
      return;
    }
    setFileError(null);
    setDraftFile(file);
    setSelectedAiIdx(null);
  }

  function handleApply() {
    if (draftFile) onFileSelect(draftFile);
    if (enableIcon) {
      onIconChange(draftMode === 'icon' ? draftIcon : null);
      onBgColorChange(draftBgColor);
    }
    setOpen(false);
  }

  const TriggerIcon = enableIcon && avatarIcon ? APP_ICON_MAP[avatarIcon] : null;
  const currentBgStyle = enableIcon && avatarBgColor ? { backgroundColor: avatarBgColor } : {};
  const triggerDisplayUrl = triggerPreviewUrl ?? logoUrl;
  const draftDisplayUrl = draftMode === 'icon' ? null : (draftPreviewUrl ?? logoUrl);
  const hasDraft = !!(draftFile || (draftMode === 'icon' && draftIcon));

  return (
    <>
      {/* ── Trigger ── */}
      <button
        type="button"
        aria-label="Edit avatar"
        onClick={() => setOpen(true)}
        className="relative group/avatar size-14 overflow-hidden rounded-[var(--radius)] border flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        style={currentBgStyle}
      >
        {TriggerIcon ? (
          <TriggerIcon
            className="size-7"
            style={{ color: avatarBgColor ? '#ffffff' : 'currentColor' }}
          />
        ) : triggerDisplayUrl ? (
          <img src={triggerDisplayUrl} alt="Avatar" className="size-full object-cover" />
        ) : (
          <span className="text-base font-semibold text-muted-foreground select-none">
            {fallback}
          </span>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/avatar:opacity-100 transition-opacity">
          <Pencil className="size-4 text-white" />
        </span>
      </button>

      {pendingFile && (
        <span className="mt-1 text-[10px] font-medium text-primary">pending upload</span>
      )}

      {/* ── Drawer (mobile) / Dialog (desktop) ── */}
      <FormDrawer
        open={open}
        onOpenChange={setOpen}
        title={title}
        onSubmit={handleApply}
        submitLabel="Apply"
        isDirty={hasDraft}
      >
        <div className="space-y-5">
          {/* Avatar + primary controls row */}
          <div className="flex items-start gap-4">
            {/* Avatar preview — hover overlay to upload */}
            <div className="relative group/preview shrink-0">
              <AvatarPreview
                displayUrl={draftDisplayUrl}
                avatarIcon={draftMode === 'icon' ? draftIcon : null}
                avatarBgColor={draftBgColor}
                fallback={fallback}
                size="md"
              />
              {draftMode === 'image' && (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="absolute inset-0 rounded-[var(--radius)] flex flex-col items-center justify-center gap-0.5 bg-black/50 opacity-0 group-hover/preview:opacity-100 transition-opacity"
                  aria-label="Upload image"
                >
                  <ImagePlus className="size-4 text-white" />
                  <span className="text-[10px] text-white font-medium leading-none">
                    {draftDisplayUrl ? 'Replace' : 'Upload'}
                  </span>
                </button>
              )}
            </div>

            <div className="flex-1 space-y-2.5">
              {/* Image/Icon mode toggle */}
              {enableIcon && (
                <div className="inline-flex rounded-md border bg-muted p-0.5 text-sm">
                  {(['image', 'icon'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDraftMode(m)}
                      className={`rounded px-3 py-1 capitalize transition-colors ${
                        draftMode === m
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}

              {/* Replace / Choose image */}
              {draftMode === 'image' && (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ImagePlus className="size-4 shrink-0" />
                  {(draftPreviewUrl ?? logoUrl) ? 'Replace image' : 'Choose image'}
                </button>
              )}

              {/* Use AI to generate — checkbox */}
              {draftMode === 'image' && aiGenerate && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={useAiGenerate}
                    onChange={(e) => setUseAiGenerate(e.target.checked)}
                    className="size-4 rounded border-border accent-primary"
                  />
                  <span className="text-sm text-foreground">Use AI to generate</span>
                </label>
              )}

              {/* Remove image */}
              {draftMode === 'image' && (logoUrl || draftFile) && onClearImage && (
                <button
                  type="button"
                  onClick={() => {
                    setDraftFile(null);
                    setDraftPreviewUrl(null);
                    setSelectedAiIdx(null);
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="size-3" />
                  Remove image
                </button>
              )}
              {fileError && <p className="text-xs text-destructive">{fileError}</p>}

              {enableIcon && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Background</p>
                  <div className="flex flex-wrap gap-1.5">
                    {BG_SWATCHES.map(({ label, value }) => (
                      <button
                        key={label}
                        type="button"
                        title={label}
                        onClick={() => setDraftBgColor(value)}
                        className={`size-5 rounded border-2 transition-all ${
                          (draftBgColor ?? '') === value
                            ? 'border-foreground scale-110'
                            : 'border-transparent hover:border-muted-foreground/50'
                        }`}
                        style={
                          value
                            ? { backgroundColor: value }
                            : {
                                background:
                                  'repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 0 0 / 6px 6px',
                              }
                        }
                      />
                    ))}
                    <button
                      type="button"
                      title="Custom color"
                      onClick={() => colorRef.current?.click()}
                      className={`size-5 rounded border-2 overflow-hidden transition-all ${
                        draftBgColor && !BG_SWATCHES.find((s) => s.value === draftBgColor)
                          ? 'border-foreground scale-110'
                          : 'border-dashed border-muted-foreground/40 hover:border-muted-foreground/60'
                      }`}
                      style={
                        draftBgColor && !BG_SWATCHES.find((s) => s.value === draftBgColor)
                          ? { backgroundColor: draftBgColor }
                          : {}
                      }
                    >
                      {(!draftBgColor || BG_SWATCHES.find((s) => s.value === draftBgColor)) && (
                        <span className="text-[9px] text-muted-foreground">+</span>
                      )}
                    </button>
                    <input
                      ref={colorRef}
                      type="color"
                      className="sr-only"
                      value={draftBgColor || '#000000'}
                      onChange={(e) => setDraftBgColor(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI generation panel — full-width below the avatar row */}
          {draftMode === 'image' && aiGenerate && useAiGenerate && (
            <div className="space-y-3 rounded-xl border bg-card p-4">
              {/* Description textarea — user describes the entity, BFF builds full prompt */}
              <div className="relative">
                <Textarea
                  rows={4}
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  disabled={aiGenerating}
                  placeholder="Describe what this agent / entity does…"
                  className="resize-none pr-8"
                  style={{ fontSize: '16px' }}
                />
                <button
                  type="button"
                  aria-label="Expand description editor"
                  onClick={() => setPromptExpanded(true)}
                  className="absolute top-2 right-2 flex items-center justify-center size-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Expand className="size-3.5" />
                </button>
              </div>

              {/* Expanded description — Drawer on mobile, Dialog on desktop */}
              {isDesktop ? (
                <Dialog open={promptExpanded} onOpenChange={setPromptExpanded}>
                  <DialogContent className="flex flex-col gap-0 p-0 max-w-2xl h-[70vh]">
                    <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
                      <DialogTitle>Edit description</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 p-4">
                      <Textarea
                        value={aiDescription}
                        onChange={(e) => setAiDescription(e.target.value)}
                        placeholder="Describe what this agent / entity does…"
                        className="h-full resize-none"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Drawer open={promptExpanded} onOpenChange={setPromptExpanded}>
                  <DrawerContent className="h-[80svh] flex flex-col overflow-hidden">
                    <DrawerHeader className="shrink-0 border-b border-border px-4 py-3 text-left">
                      <DrawerTitle>Edit description</DrawerTitle>
                    </DrawerHeader>
                    <div className="flex-1 min-h-0 p-4">
                      <Textarea
                        value={aiDescription}
                        onChange={(e) => setAiDescription(e.target.value)}
                        placeholder="Describe what this agent / entity does…"
                        className="h-full resize-none"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </DrawerContent>
                </Drawer>
              )}

              {/* Model + count selects — stacked on mobile, row on sm+ */}
              <div className="flex flex-col gap-2 sm:flex-row">
                {imageModels.length > 0 && (
                  <Select value={aiModel} onValueChange={setAiModel} disabled={aiGenerating}>
                    <SelectTrigger className="h-8 text-xs sm:flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {imageModels.map((m) => (
                        <SelectItem key={m.modelName} value={m.modelName} className="text-xs">
                          {m.displayName ?? m.modelName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select
                  value={String(aiCount)}
                  onValueChange={(v) => setAiCount(Number(v))}
                  disabled={aiGenerating}
                >
                  <SelectTrigger className="h-8 text-xs sm:w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map((n) => (
                      <SelectItem key={n} value={String(n)} className="text-xs">
                        {n} image{n > 1 ? 's' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Generate button */}
              <button
                type="button"
                onClick={() => void handleAiGenerate()}
                disabled={aiGenerating}
                className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors disabled:opacity-40"
              >
                {aiGenerating ? (
                  <Spinner className="size-4 shrink-0" />
                ) : (
                  <Sparkles className="size-4 shrink-0" />
                )}
                {aiGenerating
                  ? 'Generating…'
                  : aiImages.length > 0
                    ? 'Generate another'
                    : 'Generate'}
              </button>

              {/* Error display */}
              {aiError && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2 text-xs text-destructive space-y-0.5">
                  <p className="font-medium">{aiError.message}</p>
                  {(aiError.code || aiError.traceId) && (
                    <p className="font-mono text-[10px] text-destructive/70">
                      {[aiError.code, aiError.traceId && `trace:${aiError.traceId}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI-generated image gallery */}
          {draftMode === 'image' && aiImages.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">
                AI generated — tap to select
              </p>
              <div className="grid grid-cols-3 gap-2">
                {aiImages.map((img, idx) => (
                  <button
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable generation order
                    key={idx}
                    type="button"
                    onClick={() => selectAiImage(idx)}
                    className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                      selectedAiIdx === idx
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-transparent hover:border-border'
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={`Generated option ${idx + 1}`}
                      className="size-full object-cover"
                    />
                  </button>
                ))}
                {/* Generating placeholder */}
                {aiGenerating && (
                  <div className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                    <Spinner className="size-5 text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Icon picker grid */}
          {enableIcon && draftMode === 'icon' && (
            <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
              {APP_ICON_CATEGORIES.map((category) => (
                <div key={category.label}>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {category.label}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {category.icons.map(({ name, Icon }) => (
                      <button
                        key={name}
                        type="button"
                        title={name}
                        onClick={() => setDraftIcon(name)}
                        className={`flex size-8 items-center justify-center rounded-md border transition-all ${
                          draftIcon === name
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-transparent hover:border-border hover:bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Icon className="size-4" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
          }}
        />
      </FormDrawer>

      {/* Cleanup blob URLs on unmount */}
    </>
  );
}
